/**
 * Wiggly Stuff — Wishlist
 * localStorage-backed wishlist. No backend required.
 * Storage key: wiggly_wishlist → JSON array of product handles
 */

(function () {
  'use strict';

  var STORAGE_KEY = 'wiggly_wishlist';

  // ─── Store ──────────────────────────────────────────────────────────────────

  var WishlistStore = {
    get: function () {
      try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
      } catch (e) {
        return [];
      }
    },
    save: function (list) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
      } catch (e) {
        // localStorage unavailable (private browsing, storage full, etc.)
      }
    },
    has: function (handle) {
      return this.get().indexOf(handle) !== -1;
    },
    add: function (handle) {
      var list = this.get();
      if (list.indexOf(handle) === -1) {
        list.push(handle);
        this.save(list);
        this._emit();
      }
    },
    remove: function (handle) {
      var list = this.get().filter(function (h) { return h !== handle; });
      this.save(list);
      this._emit();
    },
    toggle: function (handle) {
      if (this.has(handle)) {
        this.remove(handle);
        return false;
      } else {
        this.add(handle);
        return true;
      }
    },
    count: function () {
      return this.get().length;
    },
    _emit: function () {
      document.dispatchEvent(new CustomEvent('wishlist:changed'));
    }
  };

  // ─── UI ─────────────────────────────────────────────────────────────────────

  var WishlistUI = {
    init: function () {
      var self = this;

      // Bind all heart buttons already in DOM
      document.querySelectorAll('[data-wishlist-btn]').forEach(function (btn) {
        self.bindBtn(btn);
      });

      // Bind hearts on cards added later (e.g. collection "Load more"), mirroring
      // the variant-selector observer in custom.js.
      var observer = new MutationObserver(function (mutations) {
        var added = false;
        mutations.forEach(function (mutation) {
          if (mutation.addedNodes.length) added = true;
        });
        if (!added) return;
        document
          .querySelectorAll('[data-wishlist-btn]:not([data-wishlist-bound])')
          .forEach(function (btn) {
            self.bindBtn(btn);
          });
      });
      observer.observe(document.body, { childList: true, subtree: true });

      // Keep nav badge in sync
      document.addEventListener('wishlist:changed', function () {
        self.syncNav();
      });

      // Initial sync
      self.syncNav();

      // If we're on the wishlist page, load products
      if (document.querySelector('[data-wishlist-page]')) {
        self.renderPage();
      }
    },

    bindBtn: function (btn) {
      if (btn.getAttribute('data-wishlist-bound')) return;
      var handle = btn.getAttribute('data-product-handle');
      if (!handle) return;
      btn.setAttribute('data-wishlist-bound', 'true');

      // Set initial state
      this._updateBtnState(btn, WishlistStore.has(handle));

      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var added = WishlistStore.toggle(handle);
        WishlistUI._updateBtnState(btn, added);

        // If on wishlist page and removing, fade out the card
        if (!added) {
          var card = btn.closest('[data-wishlist-card]');
          if (card) {
            card.style.transition = 'opacity 0.3s';
            card.style.opacity = '0';
            setTimeout(function () {
              card.remove();
              WishlistUI._updatePageCount();
              WishlistUI._checkEmpty();
            }, 310);
          }
        }
      });
    },

    _updateBtnState: function (btn, isWishlisted) {
      if (isWishlisted) {
        btn.classList.add('is-wishlisted');
        btn.setAttribute('aria-label', 'Remove from wishlist');
        btn.setAttribute('title', 'Remove from wishlist');
      } else {
        btn.classList.remove('is-wishlisted');
        btn.setAttribute('aria-label', 'Add to wishlist');
        btn.setAttribute('title', 'Add to wishlist');
      }
    },

    syncNav: function () {
      var count = WishlistStore.count();
      document.querySelectorAll('[data-wishlist-nav-badge]').forEach(function (badge) {
        badge.textContent = count;
        if (count > 0) {
          badge.classList.remove('d-none');
        } else {
          badge.classList.add('d-none');
        }
      });
    },

    // ── Wishlist page ──────────────────────────────────────────────────────────

    renderPage: function () {
      var sharedHandles = WishlistUI._parseSharedParam();
      var isShared = sharedHandles.length > 0;
      var handles = isShared ? sharedHandles : WishlistStore.get();
      var grid = document.querySelector('[data-wishlist-grid]');
      var emptyState = document.querySelector('[data-wishlist-empty]');
      var sidebar = document.querySelector('[data-wishlist-sidebar]');
      var sharedBanner = document.querySelector('[data-wishlist-shared-banner]');
      var viewMineLink = document.querySelector('[data-wishlist-view-mine]');
      var mergeBtn = document.querySelector('[data-wishlist-merge]');
      var addAllBtn = document.querySelector('[data-wishlist-add-all]');
      var clearAllBtn = document.querySelector('[data-wishlist-clear-all]');
      var shareBtn = document.querySelector('[data-wishlist-share]');
      var emailBtn = document.querySelector('[data-wishlist-email]');

      if (!grid) return;

      if (isShared) {
        if (sharedBanner) sharedBanner.classList.remove('d-none');
        if (viewMineLink) viewMineLink.setAttribute('href', window.location.pathname);
        if (clearAllBtn) clearAllBtn.classList.add('d-none');
        if (shareBtn) shareBtn.classList.add('d-none');
        if (emailBtn) emailBtn.classList.add('d-none');
        if (mergeBtn) this._bindMerge(mergeBtn, sharedHandles);
      }

      if (handles.length === 0) {
        this._showEmpty(emptyState, sidebar);
        return;
      }

      var self = this;

      // Separate handles into pool hits and misses
      var poolHits = [];
      var poolMisses = [];

      handles.forEach(function (handle) {
        var slot = document.querySelector('[data-wishlist-slot="' + handle + '"]');
        if (slot) {
          poolHits.push({ handle: handle, slot: slot });
        } else {
          poolMisses.push(handle);
        }
      });

      // Clone pool cards immediately
      poolHits.forEach(function (item) {
        var clone = item.slot.cloneNode(true);
        clone.removeAttribute('data-wishlist-slot');
        clone.setAttribute('data-wishlist-card', '');
        clone.querySelectorAll('[data-wishlist-btn]').forEach(function (btn) {
          self.bindBtn(btn);
        });
        if (!isShared) {
          self._appendRemoveLink(clone, item.handle);
        }
        grid.appendChild(clone);
      });

      // For pool misses, fetch from API — or clean up stale handles
      var fetchPromises = poolMisses.map(function (handle) {
        return fetch('/products/' + handle + '.js')
          .then(function (r) {
            if (!r.ok) {
              if (!isShared) {
                // Product no longer exists — remove from wishlist silently
                WishlistStore.remove(handle);
              }
              return;
            }
            return r.json();
          })
          .then(function (product) {
            if (!product) return;
            var card = self._buildFallbackCard(product);
            card.querySelectorAll('[data-wishlist-btn]').forEach(function (btn) {
              self.bindBtn(btn);
            });
            if (!isShared) {
              self._appendRemoveLink(card, handle);
            }
            grid.appendChild(card);
          })
          .catch(function () {
            if (!isShared) {
              WishlistStore.remove(handle);
            }
          });
      });

      Promise.all(fetchPromises).then(function () {
        var validCount = grid.querySelectorAll('[data-wishlist-card]').length;
        self._updatePageCount(validCount);

        if (validCount === 0) {
          self._showEmpty(emptyState, sidebar);
          return;
        }

        if (emptyState) emptyState.classList.add('d-none');
        if (sidebar) sidebar.classList.remove('d-none');

        if (addAllBtn) self._bindAddAll(addAllBtn, grid);

        if (isShared) {
          return;
        }

        if (clearAllBtn) self._bindClearAll(clearAllBtn, grid, emptyState, sidebar);
        if (shareBtn) self._bindShare(shareBtn);
        if (emailBtn) self._bindEmail(emailBtn, grid);
      });
    },

    _parseSharedParam: function () {
      try {
        var raw = new URLSearchParams(window.location.search).get('items');
        var handles = [];
        var seen = {};

        if (!raw) return [];

        raw.split(',').forEach(function (part) {
          var handle;
          if (handles.length >= 50) return;
          handle = part.replace(/^\s+|\s+$/g, '');
          if (!handle || !/^[a-z0-9][a-z0-9-]*$/.test(handle) || seen[handle]) return;
          seen[handle] = true;
          handles.push(handle);
        });

        return handles;
      } catch (e) {
        return [];
      }
    },

    _buildShareUrl: function () {
      return window.location.origin + window.location.pathname + '?items=' + WishlistStore.get().join(',');
    },

    _bindMerge: function (btn, handles) {
      btn.addEventListener('click', function () {
        handles.forEach(function (handle) {
          WishlistStore.add(handle);
        });
        window.location.href = window.location.pathname;
      });
    },

    _bindShare: function (btn) {
      // navigator.share and clipboard.writeText need a secure context (HTTPS).
      // On non-HTTPS previews the window.prompt fallback is expected behavior.
      btn.addEventListener('click', function () {
        var url = WishlistUI._buildShareUrl();
        // innerHTML, not textContent — the button label includes an icon element
        var originalHtml = btn.innerHTML;

        if (navigator.share) {
          try {
            var shareResult = navigator.share({
              title: 'My Wiggly Stuff Wishlist',
              url: url
            });
            if (shareResult && typeof shareResult.catch === 'function') {
              shareResult.catch(function () {});
            }
          } catch (e) {}
          return;
        }

        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(url)
            .then(function () {
              btn.textContent = 'Link Copied!';
              setTimeout(function () {
                btn.innerHTML = originalHtml;
              }, 2000);
            })
            .catch(function () {
              window.prompt('Copy your wishlist link:', url);
            });
          return;
        }

        window.prompt('Copy your wishlist link:', url);
      });
    },

    _bindEmail: function (btn, grid) {
      btn.addEventListener('click', function () {
        var lines = [];
        var subject = 'My Wiggly Stuff Wishlist 🎁';
        var body;

        grid.querySelectorAll('[data-wishlist-card]').forEach(function (card) {
          var titleLink;
          var href;
          var title;

          // Cap lines to keep the mailto URL under conservative client limits;
          // the share link below covers the full list.
          if (lines.length >= 15) return;

          titleLink = card.querySelector('.product-card-alt__title a');
          if (!titleLink) return;

          href = titleLink.getAttribute('href') || '';
          title = titleLink.textContent.replace(/^\s+|\s+$/g, '');
          lines.push('• ' + title + '\n  ' + (href.indexOf('http') === 0 ? href : window.location.origin + href) + '\n');
        });

        body = 'Here\'s my Wiggly Stuff wishlist:\n\n' + lines.join('') + '\nOpen the full list here:\n' + WishlistUI._buildShareUrl() + '\n';
        window.location.href = 'mailto:?subject=' + encodeURIComponent(subject) + '&body=' + encodeURIComponent(body);
      });
    },

    // Bind the ATC submit button inside a cloned pool card
    _bindCardAtc: function (form, card) {
      // product-card-form uses a <product-form> custom element which handles submission natively.
    },

    // Fallback card for products not in the Liquid pool — matches product-card-alt structure
    _buildFallbackCard: function (product) {
      var variant = null;
      for (var i = 0; i < product.variants.length; i++) {
        if (product.variants[i].available) { variant = product.variants[i]; break; }
      }
      if (!variant) variant = product.variants[0];

      var rawImage = product.featured_image || (product.images && product.images[0]) || '';
      var image = this._shopifyImageUrl(rawImage, 600);
      var isMulti = !(product.variants.length === 1 && product.variants[0].title === 'Default Title');
      var price = this._money(variant.price);
      var url = '/products/' + this._esc(product.handle);

      var btnStyle = 'background-color:var(--yellow);border:var(--border-width) solid var(--black)!important;border-radius:var(--border-radius)!important;color:var(--black);font-family:var(--font-bungee);text-transform:uppercase;letter-spacing:var(--bungee-letter-spacing);padding:0.5rem 0.75rem;transition:var(--transition);box-shadow:var(--shadow);';
      var btnHover = 'onmouseover="this.style.transform=\'translate(-2px,-2px)\';this.style.boxShadow=\'var(--shadow-lg)\';" onmouseout="this.style.transform=\'\';this.style.boxShadow=\'var(--shadow)\';"';

      // Always include the hidden input so Add All to Cart can pick it up.
      // For multi-variant products the first available variant is used; the modal will flag these.
      var atcHtml = isMulti
        ? '<input type="hidden" class="product-card-form-variant-input" value="' + variant.id + '">'
            + '<a href="' + url + '" class="btn btn-atc btn-sm w-100 flex-grow-1" style="' + btnStyle + '" ' + btnHover + '>Choose Options</a>'
        : '<form action="/cart/add" method="post" class="flex-grow-1" style="width:auto;">'
            + '<input type="hidden" name="id" value="' + variant.id + '" class="product-card-form-variant-input">'
            + '<div class="d-flex align-items-stretch">'
            + '<button type="submit" class="btn btn-atc btn-sm w-100 flex-grow-1" style="' + btnStyle + '" ' + btnHover + '>Add to Cart</button>'
            + '</div></form>';

      var viewBtn = '<a href="' + url + '" class="btn btn-view-product-icon btn-sm d-inline-flex align-items-center justify-content-center" aria-label="View product: ' + this._esc(product.title) + '" title="View product" style="background-color:var(--white);border:var(--border-width) solid var(--black)!important;border-radius:var(--border-radius)!important;color:var(--black);line-height:1;box-shadow:var(--shadow);transition:var(--transition);" ' + btnHover + '>'
        + '<span class="visually-hidden">View product</span>'
        + '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z"></path><circle cx="12" cy="12" r="3"></circle></svg>'
        + '</a>';

      var wrapper = document.createElement('div');
      wrapper.className = 'p-4 p-desktop-5 mb-4 mb-desktop-5';
      wrapper.setAttribute('role', 'list-item');
      wrapper.setAttribute('data-wishlist-card', '');

      wrapper.innerHTML = [
        '<div class="product-card-alt product-card text-center' + (isMulti ? ' product-card-alt--multi-variant' : '') + '">',
          '<div class="product-card-link">',
            '<div class="product-card-visual position-relative">',
              '<a href="' + url + '" class="text-decoration-none">',
                '<div class="product-card-img-wrapper position-relative" style="aspect-ratio:1/1;background-color:var(--black);">',
                  image ? '<img src="' + image + '" alt="' + this._esc(product.title) + '" class="product-card-image w-100 h-100 object-fit-cover" width="600" height="600" loading="lazy">' : '',
                '</div>',
              '</a>',
            '</div>',
            '<div class="product-card-alt__body">',
              '<h3 class="product-card-alt__title"><a href="' + url + '" class="text-decoration-none">' + this._esc(product.title) + '</a></h3>',
              '<div class="product-card-alt__footer">',
                '<div class="product-card-alt__price">',
                  '<p class="product-card-price product-price mb-4"><span class="product-card-price-final">' + price + '</span></p>',
                '</div>',
                '<div class="product-card-buttons d-flex align-items-stretch gap-2 w-100">',
                  atcHtml,
                  viewBtn,
                '</div>',
              '</div>',
            '</div>',
          '</div>',
        '</div>'
      ].join('');

      return wrapper;
    },

    _appendRemoveLink: function (card, handle) {
      var link = document.createElement('button');
      link.type = 'button';
      link.className = 'wishlist-remove-link';
      link.textContent = '× Remove from wishlist';
      link.addEventListener('click', function () {
        WishlistStore.remove(handle);
        card.style.transition = 'opacity 0.3s';
        card.style.opacity = '0';
        setTimeout(function () {
          card.remove();
          WishlistUI._updatePageCount();
          WishlistUI._checkEmpty();
        }, 310);
      });

      // Insert inside the card border, after the buttons row
      var buttonsRow = card.querySelector('.product-card-buttons');
      if (buttonsRow && buttonsRow.parentNode) {
        buttonsRow.parentNode.insertBefore(link, buttonsRow.nextSibling);
      } else {
        // fallback: append to the inner .product-card element
        var inner = card.querySelector('.product-card');
        if (inner) inner.appendChild(link);
        else card.appendChild(link);
      }
    },

    _shopifyImageUrl: function (url, size) {
      if (!url) return '';
      var sep = url.indexOf('?') !== -1 ? '&' : '?';
      return url + sep + 'width=' + size + '&height=' + size + '&crop=center';
    },

    _money: function (cents) {
      return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
    },

    _esc: function (str) {
      return String(str)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;')
        .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#x27;');
    },

    _bindAddAll: function (btn, grid) {
      btn.addEventListener('click', function () {
        var items = [];
        var multiVariantProducts = [];

        grid.querySelectorAll('[data-wishlist-card]').forEach(function (card) {
          var input = card.querySelector('input.product-card-form-variant-input');
          if (input && input.value) {
            items.push({ id: parseInt(input.value, 10), quantity: 1 });
          }
          var inner = card.querySelector('.product-card-alt--multi-variant');
          if (inner) {
            var titleEl = inner.querySelector('.product-card-alt__title a');
            if (titleEl) {
              multiVariantProducts.push({
                title: titleEl.textContent.trim(),
                url: titleEl.getAttribute('href')
              });
            }
          }
        });

        if (!items.length) return;

        btn.disabled = true;
        btn.textContent = 'Adding…';

        var CART_SECTIONS = ['cart-count-badge', 'offcanvas-cart'];

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: items, sections: CART_SECTIONS })
        })
          .then(function (r) { return r.json(); })
          .then(function (data) {
            btn.textContent = 'Added!';
            setTimeout(function () {
              btn.disabled = false;
              btn.textContent = 'Add All to Cart';
            }, 2000);

            var cartEl = document.querySelector('cart-container');
            if (cartEl && data.sections && typeof cartEl.reloadCartElements === 'function') {
              cartEl.reloadCartElements(data.sections);
            }

            if (multiVariantProducts.length > 0 && window.bootstrap) {
              var listEl = document.getElementById('wishlist-auto-variant-list');
              if (listEl) {
                listEl.innerHTML = multiVariantProducts.map(function (p) {
                  return '<li><a href="' + p.url + '" style="color:var(--black);font-weight:600;">' + p.title + '</a></li>';
                }).join('');
              }
              var autoModal = document.getElementById('wishlist-auto-variant-modal');
              if (autoModal) {
                window.bootstrap.Modal.getOrCreateInstance(autoModal).show();
              }
            } else {
              var offcanvas = document.getElementById('offcanvas-cart');
              if (offcanvas && window.bootstrap) {
                window.bootstrap.Offcanvas.getOrCreateInstance(offcanvas).show();
              }
            }
          })
          .catch(function () {
            btn.disabled = false;
            btn.textContent = 'Add All to Cart';
          });
      });
    },

    _addToCart: function (variantId, btn) {
      btn.disabled = true;
      btn.textContent = 'Adding…';
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      })
        .then(function () {
          btn.textContent = 'Added!';
          setTimeout(function () {
            btn.disabled = false;
            btn.textContent = 'Add to Cart';
          }, 1500);
          var cartToggle = document.querySelector('[aria-controls="offcanvas-cart"]');
          if (cartToggle) cartToggle.click();
        })
        .catch(function () {
          btn.disabled = false;
          btn.textContent = 'Add to Cart';
        });
    },

    _bindClearAll: function (btn, grid, emptyState, sidebar) {
      var self = this;
      var modalEl = document.getElementById('wishlist-clear-modal');
      var confirmBtn = document.getElementById('wishlist-clear-confirm');

      if (!modalEl || !confirmBtn || !window.bootstrap) return;

      var modal = window.bootstrap.Modal.getOrCreateInstance(modalEl);

      btn.addEventListener('click', function () {
        modal.show();
      });

      confirmBtn.addEventListener('click', function () {
        modal.hide();
        WishlistStore.save([]);
        WishlistStore._emit();
        grid.querySelectorAll('[data-wishlist-card]').forEach(function (card) {
          card.remove();
        });
        document.querySelectorAll('[data-wishlist-btn]').forEach(function (heartBtn) {
          self._updateBtnState(heartBtn, false);
        });
        self._showEmpty(emptyState, sidebar);
      });
    },

    _showEmpty: function (emptyState, sidebar) {
      if (emptyState) emptyState.classList.remove('d-none');
      if (sidebar) sidebar.classList.add('d-none');
      this._updatePageCount(0);
    },

    _updatePageCount: function (count) {
      if (typeof count === 'undefined') {
        count = document.querySelectorAll('[data-wishlist-card]').length;
      }
      document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
        el.textContent = count;
      });
      // Update the items label in the sidebar
      document.querySelectorAll('[data-wishlist-items-label]').forEach(function (el) {
        el.textContent = count === 1 ? 'item' : 'items';
      });
    },

    _checkEmpty: function () {
      var remaining = document.querySelectorAll('[data-wishlist-card]').length;
      if (remaining === 0) {
        var emptyState = document.querySelector('[data-wishlist-empty]');
        var sidebar = document.querySelector('[data-wishlist-sidebar]');
        this._showEmpty(emptyState, sidebar);
      }
    },

  };

  // ─── Bootstrap ──────────────────────────────────────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { WishlistUI.init(); });
  } else {
    WishlistUI.init();
  }

  // Expose for debugging
  window.WishlistStore = WishlistStore;

})();
